import React from 'react';
import './AdminProductCard.css';
import { getCreatorDisplayName } from '../utils/authUtils';
import { getPrimaryProductImageUrl } from '../../utils/productImages';

const AdminProductCard = ({
  product,
  pdpViewCount,
  onEdit,
  onDelete,
  onOpenInventoryHub,
  onManageReviews,
  reviewsReadOnly = false,
  onStatusToggle,
  onSaleSettings,
  onViewProduct,
  canToggleFeatured = true,
}) => {
  // Debug: Log product data to check badge properties
  console.log('🏷️ AdminProductCard - Product badges check:', {
    name: product.name,
    isFeatured: product.isFeatured,
    isNew: product.isNew,
    isOnSale: product.isOnSale,
    isActive: product.isActive
  });

  // Get product image (pinned / primary when set)
  const getImageUrl = () => {
    const primary = getPrimaryProductImageUrl(product);
    if (primary) return primary;
    if (product.image) {
      if (typeof product.image === 'string') {
        return product.image;
      } else if (product.image.url) {
        return product.image.url;
      } else if (product.image.fullPath) {
        return product.image.fullPath;
      } else if (product.image.src) {
        return product.image.src;
      } else if (product.image.path) {
        return product.image.path;
      }
    }
    if (product.imageUrl) {
      return product.imageUrl;
    }
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (typeof firstImage === 'string') {
        return firstImage;
      }
      if (firstImage.url) {
        return firstImage.url;
      }
      if (firstImage.fullPath) {
        return firstImage.fullPath;
      }
      if (firstImage.src) {
        return firstImage.src;
      }
      if (firstImage.path) {
        return firstImage.path;
      }
    }
    if (product.thumbnail) {
      return product.thumbnail;
    }
    if (product.photo) {
      return product.photo;
    }
    return null;
  };

  const imageUrl = getImageUrl();

  const formatPrice = (price) => {
    if (!price || typeof price !== 'number') {
      return 'Rs. 0';
    }
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="admin-product-card">
      {/* Image Section - Top */}
      <div className="admin-product-image-container">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={product.name || 'Product Image'}
            className="admin-product-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="admin-product-image-placeholder">
            <i className="fas fa-image"></i>
            <span>No Image</span>
          </div>
        )}
        
        {/* Overlay on Hover */}
        <div className="admin-product-overlay">
          {onEdit && (
            <button 
              className="admin-action-btn admin-action-edit"
              onClick={() => onEdit(product)}
              title="Edit Product"
            >
              <i className="fas fa-edit"></i>
            </button>
          )}
          {onDelete && (
            <button 
              className="admin-action-btn admin-action-delete"
              onClick={() => onDelete(product)}
              title="Delete Product"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
        
        {/* Badges */}
        <div className="admin-product-badges">
          {product.isFeatured && (
            <span className="admin-badge admin-badge-featured">
              <i className="fas fa-star"></i>
              Featured
            </span>
          )}
          {product.isNew && (
            <span className="admin-badge admin-badge-new">
              <i className="fas fa-sparkles"></i>
              New
            </span>
          )}
          {product.isOnSale && (
            <span className="admin-badge admin-badge-sale">
              <i className="fas fa-tag"></i>
              Sale
            </span>
          )}
          {product.isActive === false && (
            <span className="admin-badge admin-badge-inactive">
              <i className="fas fa-pause"></i>
              Inactive
            </span>
          )}
          {pdpViewCount != null && (
            <span className="admin-badge admin-badge-views">
              <i className="fas fa-eye"></i>
              {Number(pdpViewCount).toLocaleString()} views
            </span>
          )}
        </div>
      </div>
      
      {/* Details Section - Bottom */}
      <div className="admin-product-info">
        {/* Product Name */}
        <h3 className="admin-product-name">{product.name || 'Unnamed Product'}</h3>
        
        {(product.category || product.mainCategory || product.createdBy) && (
          <div className="admin-product-category-creator-row">
            {(product.category || product.mainCategory) && (
              <div className="admin-product-category" title="Category">
                {typeof product.category === 'string'
                  ? product.category
                  : product.category?.name || product.mainCategory || 'Unknown'}
              </div>
            )}
            {product.createdBy && (
              <div className="admin-product-created-by" title="Product creator">
                <i className="fas fa-user-edit" aria-hidden="true" />
                <span>{getCreatorDisplayName(product.createdBy) || 'Team Member'}</span>
              </div>
            )}
          </div>
        )}
        
        {pdpViewCount != null && (
          <div className="admin-product-pdp-views" title="Product detail page views">
            <i className="fas fa-eye" aria-hidden="true" />
            <span>{Number(pdpViewCount).toLocaleString()} PDP views</span>
          </div>
        )}

        {/* Price */}
        <div className="admin-product-price">
          {product.isOnSale && product.originalPrice && product.originalPrice > product.price ? (
            <>
              <span className="admin-sale-price">Rs. {product.price?.toLocaleString() || '0'}</span>
              <span className="admin-original-price">Rs. {product.originalPrice.toLocaleString()}</span>
            </>
          ) : (
            <span className="admin-current-price">Rs. {product.price?.toLocaleString() || '0'}</span>
          )}
        </div>
        
        {/* Status Toggle Buttons */}
        <div className="admin-product-status-controls">
          {canToggleFeatured && (
            <button 
              className={`admin-status-toggle-btn featured ${product.isFeatured ? 'active' : ''}`}
              onClick={() => onStatusToggle && onStatusToggle(product.id || product._id, 'isFeatured')}
              title="Toggle Featured Status"
            >
              <i className="fas fa-star"></i>
              Featured
            </button>
          )}
          <button 
            className={`admin-status-toggle-btn new ${product.isNew ? 'active' : ''}`}
            onClick={() => onStatusToggle && onStatusToggle(product.id || product._id, 'isNew')}
            title="Toggle New Arrival Status"
          >
            <i className="fas fa-sparkles"></i>
            New
          </button>
          <button 
            className={`admin-status-toggle-btn sale ${product.isOnSale ? 'active' : ''}`}
            onClick={() => onSaleSettings && onSaleSettings(product)}
            title="Configure Sale Settings"
          >
            <i className="fas fa-tag"></i>
            Sale
          </button>
          <button 
            className={`admin-status-toggle-btn active-status ${product.isActive !== false ? 'active' : ''}`}
            onClick={() => onStatusToggle && onStatusToggle(product.id || product._id, 'isActive')}
            title="Toggle Active Status"
          >
            <i className="fas fa-check-circle"></i>
            Active
          </button>
        </div>
        
        {/* Meta Info */}
        <div className="admin-product-meta">
          {(() => {
            // Calculate total stock from stock object (old API structure)
            let totalStock = 0;
            if (product.stock) {
              if (typeof product.stock === 'number') {
                totalStock = product.stock;
              } else if (typeof product.stock === 'object') {
                // Old structure: stock is an object with color/size combinations
                if (product.sizes && Array.isArray(product.sizes)) {
                  // New structure: stock[color][size]
                  Object.values(product.stock).forEach(colorStock => {
                    if (typeof colorStock === 'object') {
                      Object.values(colorStock).forEach(sizeStock => {
                        totalStock += sizeStock || 0;
                      });
                    } else {
                      totalStock += colorStock || 0;
                    }
                  });
                } else {
                  // Old structure: direct stock values
                  Object.values(product.stock).forEach(stock => {
                    totalStock += stock || 0;
                  });
                }
              }
            }
            
            // Generate product URL for SKU link
            const generateProductUrl = () => {
              if (product.url) {
                // If URL is complete, use as-is
                if (product.url.startsWith('http')) {
                  return product.url;
                }
                // If URL is relative, make it absolute
                return `${window.location.origin}${product.url.startsWith('/') ? '' : '/'}${product.url}`;
              }
              // Fallback to ID-based URL
              const productId = product.id || product._id || product.productId;
              if (productId) {
                return `${window.location.origin}/product/${productId}`;
              }
              return null;
            };

            const productUrl = generateProductUrl();
            
            return (
              <>
                <div className="admin-product-stock">
                  <span className="admin-stock">Stock: {totalStock > 0 ? totalStock : 'Out'}</span>
                </div>
                {product.sku && (
                  <div className="admin-product-sku">
                    <a 
                      href={productUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="admin-sku-link"
                      onClick={(e) => {
                        if (!productUrl) {
                          e.preventDefault();
                        }
                      }}
                      title="Open product in new window"
                    >
                      SKU: {product.sku}
                    </a>
                  </div>
                )}
              </>
            );
          })()}
        </div>
        
        {/* Actions */}
        <div className="admin-product-actions">
          {onEdit && (
            <button 
              className="admin-product-action-btn admin-product-action-edit"
              onClick={() => onEdit(product)}
              title="Edit Product"
            >
              <i className="fas fa-edit" aria-hidden="true" />
              <span className="admin-product-action-label">Edit</span>
            </button>
          )}
          {onOpenInventoryHub && (
            <button
              type="button"
              className="admin-product-action-btn admin-product-action-hub"
              onClick={() => onOpenInventoryHub(product)}
              title="Gallery & stock — images and inventory in one place"
            >
              <i className="fas fa-layer-group" aria-hidden="true" />
              <span className="admin-product-action-label admin-product-action-label--hub-long">Gallery &amp; stock</span>
              <span className="admin-product-action-label admin-product-action-label--hub-short">Gallery</span>
            </button>
          )}
          {onManageReviews && (
            <button
              type="button"
              className="admin-product-action-btn admin-product-action-reviews"
              onClick={() => onManageReviews(product)}
              title={reviewsReadOnly ? 'View customer reviews' : 'Manage customer reviews'}
            >
              <i className="fas fa-star" aria-hidden="true" />
              <span className="admin-product-action-label">Reviews</span>
            </button>
          )}
          {onDelete && (
            <button 
              className="admin-product-action-btn admin-product-action-delete"
              onClick={() => onDelete(product)}
              title="Delete Product"
            >
              <i className="fas fa-trash" aria-hidden="true" />
              <span className="admin-product-action-label">Delete</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProductCard;

